import { useEffect, useRef, useState } from "react";

interface TournamentSoundsProps {
    isTournamentRunning: boolean;
    currentRound: number;
    hasActiveGameInRound: boolean;
    startTournamentSound: string;
    newRoundSound: string;
    wasRedirectedAtStart: boolean;
    isParticipant: boolean;
    tournamentId: string;
}

const TournamentSounds: React.FC<TournamentSoundsProps> = ({
    isTournamentRunning,
    currentRound,
    hasActiveGameInRound,
    startTournamentSound,
    newRoundSound,
    wasRedirectedAtStart,
    isParticipant,
    tournamentId
}) => {
    const [isStartSoundPlaying, setIsStartSoundPlaying] = useState(false);
    const [isRoundSoundPlaying, setIsRoundSoundPlaying] = useState(false);


    const globalPlayingState = useRef<{ isPlaying: boolean; timeoutId: NodeJS.Timeout | null }>({
        isPlaying: false,
        timeoutId: null
    });

    const playSound = async (audio: HTMLAudioElement, soundType: 'start' | 'round') => {
        if (globalPlayingState.current.isPlaying) {
            return;
        }
        audio.src = soundType === 'start' ? startTournamentSound : newRoundSound;
        audio.load();

        try {
            globalPlayingState.current.isPlaying = true;
            audio.currentTime = 0;
            audio.volume = 0.5;

            const playPromise = audio.play();
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        audio.onended = () => {
                            globalPlayingState.current.isPlaying = false;
                            if (soundType === 'start') {
                                setIsStartSoundPlaying(false);
                            } else {
                                setIsRoundSoundPlaying(false);
                            }
                        };
                    })
                    .catch(() => {
                        globalPlayingState.current.isPlaying = false;
                        if (soundType === 'start') {
                            setIsStartSoundPlaying(false);
                        } else {
                            setIsRoundSoundPlaying(false);
                        }
                    });
            }
        } catch {
            globalPlayingState.current.isPlaying = false;
            if (soundType === 'start') {
                setIsStartSoundPlaying(false);
            } else {
                setIsRoundSoundPlaying(false);
            }
        }
    };

    useEffect(() => {
        if (!isTournamentRunning || !isParticipant) {
            return;
        }

        const startKey = `tournament_start_${tournamentId}`;
        const wasStarted = localStorage.getItem(startKey);

        if (!wasStarted) {
            localStorage.setItem(startKey, "true");
            playSound(document.getElementById('sound_player') as HTMLAudioElement, 'start');
        }

        return () => {
            if (!isTournamentRunning) {
                localStorage.removeItem(startKey);
            }
        };
    }, [isTournamentRunning, isParticipant, tournamentId]);

    useEffect(() => {
        if (!isTournamentRunning || !hasActiveGameInRound || !isParticipant) {
            return;
        }

        const roundKey = `round_${currentRound}_${tournamentId}`;
        const wasRoundPlayed = sessionStorage.getItem(roundKey);

        if (!wasRoundPlayed && currentRound > 1) {
            sessionStorage.setItem(roundKey, "true");
            playSound((document.getElementById('sound_player') as HTMLAudioElement), 'round');
        }

    }, [currentRound, hasActiveGameInRound, isTournamentRunning, wasRedirectedAtStart, isParticipant, tournamentId]);

    useEffect(() => {
        return () => {
            (document.getElementById('sound_player') as HTMLAudioElement)?.pause();
            setIsStartSoundPlaying(false);
            setIsRoundSoundPlaying(false);
            globalPlayingState.current.isPlaying = false;
        };
    }, []);

    return null;
};

export default TournamentSounds;